/*
 * Copyright 2026 North Carolina State University
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, FormControl, FormControlLabel,
  FormGroup, FormHelperText, FormLabel, IconButton, MenuItem, Radio, RadioGroup, TextField, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { toast } from 'react-toastify';
import { ipmApi, fileUrl, useLookup, loadLookup, errorMessage } from '../api';

/* ── Number, optionally with a unit picker ── */
const NumberInput = ({ label, units, value, onChange, required, error, helperText, size }) => {
  const hasUnits = units?.length > 0;
  const num = hasUnits ? (value?.value ?? '') : (value ?? '');
  const unit = hasUnits ? (value?.unit || units[0]) : null;

  const emit = (n, u) => {
    const parsed = n === '' ? null : Number(n);
    if (!hasUnits) return onChange(parsed);
    onChange(parsed === null ? null : { value: parsed, unit: u });
  };

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <TextField
        fullWidth size={size} type="number" label={label} required={required}
        value={num} error={error} helperText={helperText}
        onChange={e => emit(e.target.value, unit)}
        inputProps={{ step: 'any' }}
      />
      {hasUnits && (
        <TextField
          select size={size} label="Unit" value={unit} sx={{ minWidth: 100 }}
          onChange={e => emit(num, e.target.value)}
        >
          {units.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
        </TextField>
      )}
    </Box>
  );
};

/* ── Compartment / project / person pickers ── */
const toOption = {
  chambers: c => ({ id: c._id, name: c.chamber, detail: c.facility_name }),
  projects: p => ({ id: p._id, name: `${p.project_id} — ${p.project_title}`, detail: p.project_status }),
  people: u => ({ id: u._id, name: u.name, detail: u.email }),
  controls: c => ({ id: c._id, name: c.name }),
};
const lookupFor = { chamber: 'chambers', project: 'projects', person: 'people', control: 'controls' };
// Rooms that aren't compartments, people without an account (a contractor,
// say) and products not yet on the List of Controls can be typed in.
const TYPE_IN = new Set(['chamber', 'person', 'control']);
const ref = (o) => (typeof o === 'string' ? { name: o.trim() } : { id: o.id, name: o.name });

/** Compartments active on the given projects (null = no filter in use). */
const useProjectChambers = (projectIds) => {
  const key = projectIds?.join(',') ?? null;
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (key === null) { setItems(null); return undefined; }
    let alive = true;
    Promise.all(key.split(',').filter(Boolean).map(id => loadLookup(`projects/${id}/chambers`)))
      .then(lists => {
        const seen = new Map();
        lists.flat().forEach(c => seen.set(c._id, c));
        if (alive) setItems([...seen.values()]);
      })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [key]);
  return items;
};

const ReferenceInput = ({ field, value, onChange, error, helperText, size, values }) => {
  const kind = lookupFor[field.type];
  // A compartment field can follow a project field: only that project's active compartments.
  const projectIds = field.type === 'chamber' && field.project_field
    ? (Array.isArray(values?.[field.project_field]) ? values[field.project_field] : [values?.[field.project_field]])
      .map(p => p?.id).filter(Boolean)
    : null;
  const projectChambers = useProjectChambers(projectIds);
  const allItems = useLookup(kind, !projectIds);
  const items = projectIds ? (projectChambers || []) : allItems;

  // When the project changes, drop compartments that aren't on the new one (typed-in rooms stay).
  const projectKey = projectIds?.join(',');
  const lastProjectKey = useRef(projectKey);
  useEffect(() => {
    if (!projectIds || projectChambers === null || lastProjectKey.current === projectKey) return;
    lastProjectKey.current = projectKey;
    const allowed = new Set(projectChambers.map(c => c._id));
    const keep = (v) => !v?.id || allowed.has(v.id);
    if (field.multiple) {
      const next = (value || []).filter(keep);
      if (next.length !== (value || []).length) onChange(next);
    } else if (value && !keep(value)) onChange(null);
  }, [projectKey, projectChambers]); // eslint-disable-line react-hooks/exhaustive-deps

  const followHint = projectIds && (projectIds.length ? (projectChambers?.length === 0 ? 'No active compartments on this project — a room can still be typed in.' : null) : 'Pick a project first to see its compartments.');
  const roleFilter = field.type === 'person' && field.roles?.length ? new Set(field.roles) : null;
  const options = (roleFilter ? items.filter(u => u.roles?.some(r => roleFilter.has(r))) : items).map(toOption[kind]);
  const freeSolo = TYPE_IN.has(field.type);

  return (
    <Autocomplete
      size={size}
      multiple={field.multiple}
      freeSolo={freeSolo}
      autoSelect={freeSolo}
      options={options}
      value={field.multiple ? (value || []) : (value || null)}
      getOptionLabel={o => (typeof o === 'string' ? o : o.name || '')}
      isOptionEqualToValue={(o, v) => (o.id && v.id ? o.id === v.id : o.name === v.name)}
      renderOption={(props, o) => (
        <li {...props} key={o.id || o.name}>
          <Box>
            <div>{o.name}</div>
            {o.detail && <Box component="span" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{o.detail}</Box>}
          </Box>
        </li>
      )}
      onChange={(e, v) => {
        if (field.multiple) onChange((v || []).map(ref).filter(x => x.name));
        else onChange(v ? ref(v) : null);
      }}
      renderInput={params => (
        <TextField {...params} label={field.label} required={field.required} error={error} helperText={(error && helperText) || followHint || helperText} />
      )}
    />
  );
};

/* ── Images ── */
const ImagesInput = ({ field, value, onChange, error, helperText }) => {
  const ids = value || [];
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const saved = await ipmApi.uploadFiles(files);
      if (saved.length < files.length) toast.warning('Some files were skipped — images only, up to 10 MB each.');
      onChange([...ids, ...saved.map(s => s._id)]);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not upload images'));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <FormControl error={error} fullWidth>
      <FormLabel required={field.required} sx={{ mb: 1 }}>{field.label}</FormLabel>
      <Box className="ipm-images">
        {ids.map(id => (
          <Box key={id} className="ipm-image-thumb">
            <a href={fileUrl(id)} target="_blank" rel="noreferrer"><img src={fileUrl(id)} alt="" /></a>
            <IconButton size="small" aria-label="Remove image" className="ipm-image-remove"
              onClick={() => onChange(ids.filter(x => x !== id))}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Button variant="outlined" component="label" disabled={busy}
          startIcon={busy ? <CircularProgress size={16} /> : <PhotoCameraIcon />}>
          {busy ? 'Uploading…' : 'Add images'}
          <input ref={inputRef} hidden type="file" accept="image/*" multiple onChange={e => handleFiles(e.target.files)} />
        </Button>
      </Box>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

/* ── Documents ── */
const FilesInput = ({ field, value, onChange, error, helperText }) => {
  const files = value || [];
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (picked) => {
    if (!picked?.length) return;
    setBusy(true);
    try {
      const saved = await ipmApi.uploadFiles(picked);
      if (saved.length < picked.length) toast.warning('Some files were skipped — PDF, image, Word, Excel or text, up to 10 MB each.');
      onChange([...files, ...saved.map(x => ({ id: x._id, name: x.filename }))]);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not upload files'));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <FormControl error={error} fullWidth>
      <FormLabel required={field.required} sx={{ mb: 1 }}>{field.label}</FormLabel>
      <Box className="ipm-images">
        {files.map(fl => (
          <Chip key={fl.id} icon={<DescriptionOutlinedIcon />} label={fl.name} component="a" href={fileUrl(fl.id)}
            target="_blank" rel="noreferrer" clickable onDelete={() => onChange(files.filter(x => x.id !== fl.id))} />
        ))}
        <Button variant="outlined" component="label" disabled={busy}
          startIcon={busy ? <CircularProgress size={16} /> : <AttachFileIcon />}>
          {busy ? 'Uploading…' : 'Attach files'}
          <input ref={inputRef} hidden type="file" multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/*" onChange={e => handleFiles(e.target.files)} />
        </Button>
      </Box>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

/* ── Table: repeating rows ── */
const speciesOptions = (items) => items.map(s => ({ id: s._id, name: s.name }));

const SpeciesCell = ({ label, value, onChange }) => {
  const options = speciesOptions(useLookup('species'));
  return (
    <Autocomplete
      size="small" options={options} value={value || null}
      getOptionLabel={o => o.name || ''}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      onChange={(e, v) => onChange(v ? ref(v) : null)}
      renderInput={params => <TextField {...params} label={label} />}
    />
  );
};

const CellInput = ({ column, value, onChange }) => {
  const common = { size: 'small', fullWidth: true, label: column.label };
  switch (column.type) {
    case 'control':
      return <ReferenceInput size="small" field={{ type: 'control', label: column.label }} value={value} onChange={onChange} />;
    case 'species':
      return <SpeciesCell label={column.label} value={value} onChange={onChange} />;
    case 'number':
      return <NumberInput size="small" label={column.label} units={column.units} value={value} onChange={onChange} />;
    case 'select':
      return (
        <TextField select {...common} value={value ?? ''} onChange={e => onChange(e.target.value)}>
          <MenuItem value=""><em>—</em></MenuItem>
          {(column.options || []).map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
      );
    case 'date':
    case 'time':
      return <TextField {...common} type={column.type} InputLabelProps={{ shrink: true }} value={value ?? ''} onChange={e => onChange(e.target.value)} />;
    default:
      return <TextField {...common} value={value ?? ''} onChange={e => onChange(e.target.value)} />;
  }
};

const TableInput = ({ field, value, onChange, error, helperText }) => {
  const rows = value || [];
  const columns = field.columns || [];
  const setCell = (i, colId, v) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [colId]: v } : r)));

  // With a species column, several species can be picked at once — one row each.
  const speciesCol = columns.find(c => c.type === 'species');
  const species = speciesOptions(useLookup('species', !!speciesCol));
  const present = new Set(rows.map(r => r[speciesCol?.id]?.id).filter(Boolean));
  const addSpecies = (picked) => onChange([...rows, ...picked.map(o => ({ [speciesCol.id]: ref(o) }))]);

  return (
    <FormControl error={error} fullWidth>
      <FormLabel required={field.required} sx={{ mb: 1 }}>{field.label}</FormLabel>
      {rows.map((row, i) => (
        <Box key={i} className="ipm-table-row">
          {columns.map(col => (
            <Box key={col.id} sx={{ flex: col.type === 'species' || (col.type === 'number' && col.units?.length) ? 2 : 1, minWidth: 140 }}>
              <CellInput column={col} value={row[col.id]} onChange={v => setCell(i, col.id, v)} />
            </Box>
          ))}
          <Tooltip title="Remove row">
            <IconButton aria-label="Remove row" onClick={() => onChange(rows.filter((_, idx) => idx !== i))}>
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        {speciesCol && (
          <Autocomplete
            multiple size="small" sx={{ minWidth: 320, flex: 1, maxWidth: 520 }}
            options={species.filter(o => !present.has(o.id))} value={[]}
            getOptionLabel={o => o.name || ''}
            onChange={(e, v) => addSpecies(v)}
            renderInput={params => <TextField {...params} label={`Add ${speciesCol.label.toLowerCase()}`} placeholder="Pick one or more" />}
          />
        )}
        <Button size="small" startIcon={<AddIcon />} onClick={() => onChange([...rows, {}])} disabled={!columns.length}>
          Add row
        </Button>
      </Box>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

/* ── The field ── */
const FieldInput = ({ field, value, onChange, error, values }) => {
  const helperText = error || field.help || undefined;
  const hasError = !!error;
  const base = { fullWidth: true, label: field.label, required: field.required, error: hasError, helperText };

  switch (field.type) {
    case 'textarea':
      return <TextField {...base} multiline minRows={3} value={value ?? ''} onChange={e => onChange(e.target.value)} />;
    case 'number':
      return <NumberInput {...base} units={field.units} value={value} onChange={onChange} />;
    case 'date':
    case 'time':
      return <TextField {...base} type={field.type} InputLabelProps={{ shrink: true }} value={value ?? ''} onChange={e => onChange(e.target.value)} />;
    case 'boolean':
      return (
        <FormControl error={hasError}>
          <FormLabel required={field.required}>{field.label}</FormLabel>
          <RadioGroup row value={value === true ? 'yes' : value === false ? 'no' : ''}
            onChange={e => onChange(e.target.value === 'yes')}>
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      );
    case 'select':
      return (
        <TextField {...base} select value={value ?? ''} onChange={e => onChange(e.target.value || null)}>
          <MenuItem value=""><em>—</em></MenuItem>
          {(field.options || []).map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
      );
    case 'multiselect':
      return (
        <Autocomplete
          multiple options={field.options || []} value={value || []}
          onChange={(e, v) => onChange(v)}
          renderInput={params => <TextField {...params} label={field.label} required={field.required} error={hasError} helperText={helperText} />}
        />
      );
    case 'checkboxes': {
      const selected = value || [];
      const toggle = (o) => onChange(selected.includes(o) ? selected.filter(x => x !== o) : [...selected, o]);
      return (
        <FormControl error={hasError}>
          <FormLabel required={field.required}>{field.label}</FormLabel>
          <FormGroup row>
            {(field.options || []).map(o => (
              <FormControlLabel key={o} label={o} control={<Checkbox checked={selected.includes(o)} onChange={() => toggle(o)} />} />
            ))}
          </FormGroup>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      );
    }
    case 'tags':
      return (
        <Autocomplete
          multiple freeSolo autoSelect options={field.options || []} value={value || []}
          onChange={(e, v) => onChange([...new Set(v.map(x => String(x).trim()).filter(Boolean))])}
          renderInput={params => <TextField {...params} label={field.label} required={field.required} error={hasError} helperText={helperText} />}
        />
      );
    case 'files':
      return <FilesInput field={field} value={value} onChange={onChange} error={hasError} helperText={helperText} />;
    case 'chamber':
    case 'project':
    case 'person':
    case 'control':
      return <ReferenceInput field={field} value={value} onChange={onChange} error={hasError} helperText={helperText} values={values} />;
    case 'images':
      return <ImagesInput field={field} value={value} onChange={onChange} error={hasError} helperText={helperText} />;
    case 'table':
      return <TableInput field={field} value={value} onChange={onChange} error={hasError} helperText={helperText} />;
    default:
      return <TextField {...base} value={value ?? ''} onChange={e => onChange(e.target.value)} />;
  }
};

export default FieldInput;
